import { kindIcon, kindLabel, kindStyle } from '@components/DocumentKind';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Box, CircularProgress, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material';

import type { ArtifactEventDocumentReference } from '../store/artifactSse';
import type { PendingExtractionSignal } from '../store/hooks';

interface PendingUploadCardProps {
  filename: string;
  /** processing_status from the artifact ack — drives the caption copy. */
  status: string;
  /** Extracted DocRef heads from the `artifact.decomposed` event. When
   *  present, the card swaps from filename to the clinical title and
   *  shows the kind badge. Empty array means decompose ran but found
   *  no clinical records (admin contracts etc.). */
  documentReferences?: ArtifactEventDocumentReference[];
  extractionSignals?: PendingExtractionSignal[];
}

/**
 * Optimistic-upload ghost. Rendered above any list/table that's about to
 * receive a fresh DocRef once the artifact pipeline finishes processing
 * — kept visually neutral so it slots in above either a plain table
 * (PatientDocumentsBlock) or a DataGrid (DocumentsListPage).
 *
 * Three visual states, in order of how much we know:
 *  1. Pre-decompose: filename + spinner + status caption
 *  2. Post-decompose with extracted DocRefs: kind badge + real title +
 *     status caption (spinner stays — enrichment isn't done yet)
 *  3. Post-decompose with no DocRefs: filename + "no records identified"
 */
export function PendingUploadCard({
  filename,
  status,
  documentReferences,
  extractionSignals = [],
}: PendingUploadCardProps) {
  const hasExtraction = documentReferences != null;
  const refs = documentReferences ?? [];
  const firstRef = refs[0];
  const moreCount = Math.max(0, refs.length - 1);

  const KindIcon = firstRef ? kindIcon(firstRef.docKind) : null;
  const kindColor = firstRef ? kindStyle(firstRef.docKind) : null;
  const kindText = firstRef ? kindLabel(firstRef.docKind) : null;

  const displayTitle = firstRef?.title ?? filename;
  const subtitle =
    hasExtraction && refs.length === 0
      ? 'No clinical records identified'
      : moreCount > 0
        ? `+ ${moreCount} more record${moreCount === 1 ? '' : 's'} · ${pendingUploadStatusLabel(status)}`
        : pendingUploadStatusLabel(status);

  const visibleSignals = extractionSignals.slice(0, 4);
  const extraSignalCount = Math.max(0, extractionSignals.length - visibleSignals.length);

  return (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1.25,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        opacity: 0.85,
      }}
    >
      {KindIcon && kindColor ? (
        <Tooltip title={kindText ?? ''}>
          <Box
            sx={{
              color: kindColor.fg,
              bgcolor: kindColor.bg,
              width: 28,
              height: 28,
              borderRadius: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <KindIcon sx={{ fontSize: 16 }} />
          </Box>
        </Tooltip>
      ) : (
        <CircularProgress size={18} />
      )}
      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
          {displayTitle}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {subtitle}
        </Typography>
        {visibleSignals.length > 0 && (
          <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', pt: 0.25 }}>
            {visibleSignals.map(signal => (
              <Box
                key={signal.id}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.35,
                  borderRadius: 999,
                  bgcolor: 'success.lighter',
                  color: 'success.dark',
                  px: 0.75,
                  py: 0.25,
                  maxWidth: 180,
                }}
              >
                <CheckCircleRoundedIcon sx={{ fontSize: 13, flexShrink: 0 }} />
                <Typography variant="caption" noWrap sx={{ fontWeight: 750 }}>
                  {signal.label}
                </Typography>
              </Box>
            ))}
            {extraSignalCount > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                +{extraSignalCount} more
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
      {firstRef ? (
        <CircularProgress size={14} sx={{ flexShrink: 0 }} />
      ) : (
        <Skeleton sx={{ width: 80, height: 14 }} />
      )}
    </Paper>
  );
}

/**
 * Caption copy for the artifact-stage skeleton. Mirrors what the timeline
 * shows so the pre-DocRef wait reads consistently across surfaces.
 */
export function pendingUploadStatusLabel(status: string): string {
  switch (status) {
    case 'uploaded':
      return 'Uploaded — extracting records…';
    case 'parsing':
      return 'Parsing pages…';
    case 'parsed':
      return 'Pages parsed — extracting fields…';
    case 'decomposed':
      return 'Records identified — enriching…';
    case 'failed':
      return 'Upload processing failed';
    default:
      return 'Processing…';
  }
}
